import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The one-line page header: title · summary · actions.
 *
 * Replaces the two-line `h1` + description block every PA page grew its own
 * version of. That block cost about 50px and spent it on a tagline — "Track and
 * manage your client payments" above a page called Payments.
 *
 * So `subtitle` is the page's *summary line*, not a description: counts and
 * totals ("62 records · last 12 months", "5 live on the public site · 1 draft").
 * It puts the number the reader came for where their eye already is. The one
 * page that genuinely needs a sentence is Overtime, where the mechanism isn't
 * self-evident from the title — a sentence there earns its place.
 *
 * Filters belong in `children` alongside the actions. They used to sit in the
 * card header below, which meant the control and the thing it controlled were
 * separated by the summary numbers.
 */
export function PageBar({
  title,
  subtitle,
  children,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Actions and filters, laid out right. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2",
        className,
      )}
    >
      <div className="flex min-w-0 items-baseline gap-2.5">
        <h1 className="text-[21px] font-bold tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        {subtitle ? (
          <>
            {/* Self-centred rather than baseline-aligned: a 13px rule sitting on
                the baseline of a 21px title reads as an underscore. */}
            <span
              aria-hidden
              className="h-[13px] w-px flex-none self-center bg-border"
            />
            <span className="min-w-0 truncate text-[13px] text-muted-foreground">
              {subtitle}
            </span>
          </>
        ) : null}
      </div>
      {children ? (
        <div className="flex flex-none items-center gap-[7px]">{children}</div>
      ) : null}
    </div>
  );
}
